import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { motion } from "framer-motion";
import { UserContext } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  PlusCircle,
  User,
  Calendar,
  MessageSquare,
} from "lucide-react";
import AuthModal from "../components/modal/AuthModal";
import useSEO from "../hooks/useSeo";

const FreelanceProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  useSEO({
    title: "Freelance Projects | Get Paid Coding Work on SolveMyCodeIssue.com",
    description:
      "Explore real freelance projects posted by clients. Bid, collaborate, and earn by solving coding issues or building complete solutions — all on SolveMyCodeIssue.com.",
    keywords:
      "freelance projects, coding jobs, freelance developers, paid coding work, programming projects, remote software jobs, Solve My Code Issue",
    canonical: "https://solvemycodeissue.com/freelance-projects",
    ogImage: "https://solvemycodeissue.com/og-image-freelance-projects.png",
  });
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const q = query(
          collection(db, "freelanceProjects"),
          orderBy("createdAt", "desc"),
          limit(20)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(data);
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handlePostProject = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    navigate("/post-freelance");
  };

  const handleDiscuss = (project) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    // 👇 Replace with your chat logic or navigate to chat page
    navigate(`/chat/${project.id}`, { state: { project } });
  };

  return (
    <Wrapper>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Freelance <Highlight>Projects</Highlight>
      </motion.h1>

      <Subtitle>
        Discover real-world freelance opportunities. Solve, collaborate, and
        grow your portfolio.
      </Subtitle>

      <ButtonRow>
        <PrimaryButton onClick={handlePostProject}>
          <PlusCircle size={20} />
          Post a Project
        </PrimaryButton>
      </ButtonRow>

      {loading ? (
        <Loading>Loading projects...</Loading>
      ) : projects.length === 0 ? (
        <Empty>No freelance projects available yet 🚀</Empty>
      ) : (
        <ProjectsGrid>
          {projects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <ProjectCard>
                <CardHeader>
                  <Briefcase size={20} color="#c59fff" />
                  <CardTitle>{p.title}</CardTitle>
                </CardHeader>

                <CardDesc>
                  {p.description?.length > 120
                    ? p.description.slice(0, 120) + "..."
                    : p.description || "No description provided"}
                </CardDesc>

                {p.skills && p.skills.length > 0 && (
                  <Skills>
                    {p.skills.map((s, idx) => (
                      <Skill key={idx}>{s}</Skill>
                    ))}
                  </Skills>
                )}

                <Meta>
                  <Small>
                    <Calendar size={14} />{" "}
                    {p.createdAt?.seconds
                      ? new Date(
                          p.createdAt.seconds * 1000
                        ).toLocaleDateString()
                      : "Recently"}
                  </Small>
                  <Budget>
                    {p.budget ? `₹${p.budget}` : "Budget not set"}
                  </Budget>
                </Meta>

                <Footer>
                  <UserInfo>
                    {p.userImage ? (
                      <Avatar src={p.userImage} />
                    ) : (
                      <User size={18} color="#c59fff" />
                    )}
                    <span>{p.postedBy || "Anonymous"}</span>
                  </UserInfo>

                  <ButtonGroup>
                    <ViewButton
                      onClick={() => navigate(`/freelance-project/${p.id}`)}
                    >
                      View
                    </ViewButton>
                    <DiscussButton onClick={() => handleDiscuss(p)}>
                      <MessageSquare size={14} />
                      Discuss
                    </DiscussButton>
                  </ButtonGroup>
                </Footer>
              </ProjectCard>
            </motion.div>
          ))}
        </ProjectsGrid>
      )}
    </Wrapper>
  );
};

export default FreelanceProjects;

/* ---------- Styles ---------- */
const Wrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
  min-height: 100vh;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #d2cde9;
  font-size: 1.1rem;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  transition: all 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;

const Loading = styled.p`
  color: #c59fff;
  font-size: 1rem;
`;

const Empty = styled.p`
  color: #ccc;
  font-size: 1.1rem;
  margin-top: 20px;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  padding: 0 20px;
  justify-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 0 10px;
  }
`;

const ProjectCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  text-align: left;
  backdrop-filter: blur(8px);
  transition: 0.3s;

  &:hover {
    transform: translateY(-5px);
    border-color: #c59fff;
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CardDesc = styled.p`
  color: #ccc;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 12px;

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Skill = styled.span`
  background: rgba(155, 92, 255, 0.2);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.8rem;
  color: #c59fff;
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const Small = styled.span`
  color: #aaa;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Budget = styled.span`
  color: #c59fff;
  font-weight: 600;
  font-size: 0.9rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  font-size: 0.9rem;
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ViewButton = styled.button`
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  border-radius: 8px;
  padding: 6px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background: #9b5cff;
    color: white;
  }
`;

const DiscussButton = styled(ViewButton)`
  display: flex;
  align-items: center;
  gap: 6px;
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
  &:hover {
    background: rgba(155, 92, 255, 0.2);
  }
`;
